import { Stack, Text, textVariants, vars } from '@etg/wings';
import { useDateFormat, useSiteContext, useTranslation } from '@eti/providers';
import { CalendarBlank } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { date as dateUtil, duration as durationUtil } from '@eti/utils';
import { css } from '@eti/styles';
import { getBoundDate } from '../../../../../../widgets/trip-details/utils/format';
import TimelineWrapper from '../../../common/TimelineWrapper';
import TimelineRow from '../common/TimelineRow';
import { getTotalTravelDuration } from '../common/utils/utils';
import { getDurationAttributes } from '../../../../../../common/time/utils';
import type { TransformedSegment } from '../../../../../../widgets/trip-itinerary/models';
import Time from '../../../../../../common/time/Time';
import useId from '../../../../../../widgets/cart/hooks/useId';

const latestDepartureDateStyles = (isDateChanged: boolean) => css`
  background-color: ${isDateChanged ? vars.colors.signal.caution.tint : 'none'};
`;

const originalDepartureDateStyles = css`
  color: #737373;
  text-decoration: line-through;
`;

interface ItineraryBoundHeaderProps {
  dataTestIdPrefix: string;
  originalDepartureDate: string | null;
  segments: TransformedSegment[];
}

const ItineraryBoundHeader = ({
  dataTestIdPrefix,
  originalDepartureDate,
  segments,
}: ItineraryBoundHeaderProps) => {
  const { t } = useTranslation();
  const { weekdayYear } = useDateFormat();
  const { language, timeFormat } = useSiteContext();
  const calendarDate = getBoundDate(segments, weekdayYear);
  const originalCalendarDate = originalDepartureDate
    ? dateUtil.format(originalDepartureDate, weekdayYear)
    : null;
  const totalTravelDuration = useMemo(() => getTotalTravelDuration(segments), [segments]);
  const localizedTravelDuration = useMemo(
    () => durationUtil.getDuration(totalTravelDuration, language.code, timeFormat),
    [language.code, timeFormat, totalTravelDuration],
  );
  const isDateChanged = originalCalendarDate !== null && calendarDate !== originalCalendarDate;

  const { ariaLabel: totalTravelDurationAriaLabel, dateTime: totalTravelDurationDateTime } =
    getDurationAttributes(language.locale, totalTravelDuration);

  const uniqueId = useId();

  return (
    <div data-testid="itinerary-bound-header">
      {Boolean(totalTravelDuration) && (
        <Text
          aria-labelledby={`${uniqueId}-itinerary-bound-header-travel-time-label`}
          as="p"
          asHtml
          data-testid={`${dataTestIdPrefix}-travel-time`}
          id={`${uniqueId}-itinerary-bound-header-travel-time-label`}
        >
          {t(
            'TripDetails.Itinerary.TravelTime',
            `<time aria-label="${totalTravelDurationAriaLabel}" datetime="${totalTravelDurationDateTime}">${localizedTravelDuration}</time>`,
          )}
        </Text>
      )}
      <TimelineRow>
        <TimelineWrapper borderColorAfter={vars.colors.inputs.border} borderStyleAfter="dashed">
          <CalendarBlank
            aria-hidden
            data-testid={`${dataTestIdPrefix}-calendar`}
            size={21}
            weight="regular"
          />
        </TimelineWrapper>
        <Stack align="start" spacing={0}>
          <Text
            as={Time}
            className={latestDepartureDateStyles(isDateChanged)}
            data-testid={`${dataTestIdPrefix}-departure-date`}
            date={Number.parseInt(getBoundDate(segments, 'T'), 10)}
            format="date"
            variant={textVariants._16_BOLD}
          >
            {calendarDate}
          </Text>
          {isDateChanged && (
            <Text
              className={originalDepartureDateStyles}
              data-testid={`${dataTestIdPrefix}-original-departure-date`}
              variant={textVariants._16_BOLD}
            >
              {originalCalendarDate}
            </Text>
          )}
        </Stack>
      </TimelineRow>
    </div>
  );
};

export default ItineraryBoundHeader;
